// TFSA calculator. Mirrors Calculator::TfsaRoom in plugins/calculator.rb --
// keep the two in sync, test/planner_parity_test.rb covers the Ruby side.

const FIRST_YEAR = 2009;
const ELIGIBILITY_AGE = 18;
const PENALTY_RATE = 0.01;

const CONTRIBUTION_MULTIPLIER = {
  weekly: 52,
  monthly: 12,
  yearly: 1
};

export const formatCurrency = (amount) => (
  Number(amount).toLocaleString("en-CA", {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })
);

export const eligibilityStartYear = (birthYear) => (
  Math.max(FIRST_YEAR, Number(birthYear) + ELIGIBILITY_AGE)
);

// Limits past the last year CRA has announced fall back to the most recent
// known limit. The UI discloses this rather than presenting it as fact.
export const limitFor = (limits, year) => {
  const known = Object.keys(limits).map(Number);
  const lastKnown = Math.max(...known);
  return limits[year] !== undefined ? limits[year] : limits[lastKnown];
};

export const cumulativeLimit = (limits, birthYear, upToYear) => {
  const startYear = eligibilityStartYear(birthYear);
  if (upToYear < startYear) return 0;

  let total = 0;
  for (let year = startYear; year <= upToYear; year += 1) {
    total += limitFor(limits, year);
  }
  return total;
};

export const availableRoom = ({ limits, birthYear, currentYear, totalContributed, priorYearWithdrawals }) => (
  cumulativeLimit(limits, birthYear, currentYear) - totalContributed + priorYearWithdrawals
);

export const overContributionPenalty = (excess, months) => (
  excess <= 0 || months <= 0 ? 0 : excess * PENALTY_RATE * months
);

// The most that can legally go in over the projection window: this year's
// available room, then one annual limit per year after that. Future limits are
// assumed flat at the last published value.
export const contributionCapacity = ({ limits, room, years, currentYear }) => (
  Math.max(room, 0) + limitFor(limits, currentYear) * Math.max(years - 1, 0)
);

export const projectGrowth = ({ startingBalance, contributionAmount, contributionFrequency, returnRate, years }) => {
  const annualReturn = returnRate / 100;
  const perYear = CONTRIBUTION_MULTIPLIER[contributionFrequency] || CONTRIBUTION_MULTIPLIER.monthly;
  const yearlyContribution = contributionAmount * perYear;

  let totalValue = startingBalance;
  let totalContributions = startingBalance;
  const yearlyData = [{ year: 0, totalValue, totalContributions, totalGrowth: 0 }];

  for (let year = 1; year <= years; year += 1) {
    totalValue += yearlyContribution;
    totalContributions += yearlyContribution;
    totalValue *= (1 + annualReturn);

    yearlyData.push({
      year,
      totalValue,
      totalContributions,
      totalGrowth: totalValue - totalContributions
    });
  }

  return yearlyData;
};

export const initTfsaCalculatorPage = () => {
  const shell = document.getElementById("tfsa-calculator");
  if (!shell) return;

  const limits = JSON.parse(shell.getAttribute("data-tfsa-limits"));
  const lastKnownYear = Math.max(...Object.keys(limits).map(Number));
  // Read the year from the browser so room stays correct without a redeploy.
  const currentYear = new Date().getFullYear();

  const form = document.getElementById("tfsa-form");
  const blocks = [
    document.getElementById("answerBand"),
    document.getElementById("roomBlock"),
    document.getElementById("growthBlock")
  ];
  const reveal = () => blocks.forEach((b) => b.classList.add("visible"));
  const el = (id) => document.getElementById(id);
  const toggle = (node, on) => node.classList.toggle("visible", on);

  const birthYearInput = el("birthYear");
  birthYearInput.max = String(currentYear - ELIGIBILITY_AGE);

  const render = () => {
    const birthYear = Number(birthYearInput.value);
    const totalContributed = Number(el("totalContributed").value) || 0;
    const priorYearWithdrawals = Number(el("priorWithdrawals").value) || 0;
    const thisYearWithdrawals = Number(el("thisYearWithdrawals").value) || 0;
    const contributionAmount = Number(el("contributionAmount").value) || 0;
    const contributionFrequency = el("contributionFrequency").value;
    const returnRate = Number(el("returnRate").value);
    const years = Math.max(Number(el("years").value) || 1, 1);

    const startYear = eligibilityStartYear(birthYear);
    // Not yet 18: no room has accrued, but the growth projection below still
    // means something, so this sets the room side to zero rather than bailing
    // out and leaving the previous render's numbers on screen.
    const notYetEligible = birthYear > currentYear - ELIGIBILITY_AGE;

    const lifetimeRoom = notYetEligible ? 0 : cumulativeLimit(limits, birthYear, currentYear);
    const room = notYetEligible ? 0 : availableRoom({
      limits, birthYear, currentYear, totalContributed, priorYearWithdrawals
    });

    el("lifetimeRoom").textContent = formatCurrency(lifetimeRoom);
    el("roomStartYear").textContent = String(startYear);
    el("roomCurrentYear").textContent = String(currentYear);

    // Over-contribution: 1%/month on the excess, for the months left this year.
    const overBy = room < 0 ? Math.abs(room) : 0;
    if (overBy > 0) {
      const monthsLeft = 12 - new Date().getMonth();
      el("penaltyAmount").textContent = formatCurrency(overContributionPenalty(overBy, monthsLeft));
      el("penaltyExcess").textContent = formatCurrency(overBy);
      el("penaltyMonths").textContent = String(monthsLeft);
    }
    toggle(el("penaltyBox"), overBy > 0);

    // Being over is the one state that needs saying in the headline. Clamping
    // it to a flat $0 there would read as "nothing left to contribute" when it
    // actually means "take money out, the CRA is charging you monthly".
    const roomAmount = el("roomAmount");
    roomAmount.textContent = overBy > 0
      ? `-${formatCurrency(overBy)}`
      : formatCurrency(room);
    roomAmount.classList.toggle("figure-value--over", overBy > 0);

    // A withdrawal made this year is not room until January 1. Naming the date
    // is the whole point of the field: it is the trap the page warns about.
    if (thisYearWithdrawals > 0) {
      el("returningAmount").textContent = formatCurrency(thisYearWithdrawals);
      el("returningYear").textContent = String(currentYear + 1);
    }
    toggle(el("returningBox"), thisYearWithdrawals > 0);

    const roomNote = el("roomNote");
    if (notYetEligible) {
      roomNote.textContent = `Room starts in ${birthYear + ELIGIBILITY_AGE}, the year you turn 18.`;
    } else if (overBy > 0) {
      roomNote.textContent = "You are over your limit. The CRA charges 1% a month until you withdraw it.";
    } else if (currentYear > lastKnownYear) {
      roomNote.textContent = `The ${currentYear} limit has not been announced yet, so this assumes it matches ${lastKnownYear} at ${formatCurrency(limitFor(limits, lastKnownYear))}.`;
    } else {
      // Name the birth year so the default figure is self-explanatory to
      // someone who lands here and has not touched the form yet. Kept short
      // because it sets the height of the headline band on a phone.
      roomNote.textContent = `Born ${birthYear}, accruing since ${startYear}.`;
    }

    // The projection compounds whatever you enter, so a plan that outruns the
    // annual limit would otherwise quietly show an illegal balance. Flagging it
    // beats silently capping, which would make the number stop matching the
    // inputs the reader typed.
    const perYear = CONTRIBUTION_MULTIPLIER[contributionFrequency] || CONTRIBUTION_MULTIPLIER.monthly;
    const plannedTotal = contributionAmount * perYear * years;
    const capacity = contributionCapacity({ limits, room, years, currentYear });
    if (plannedTotal > capacity) {
      el("capacityPlanned").textContent = formatCurrency(plannedTotal);
      el("capacityAllowed").textContent = formatCurrency(capacity);
      el("capacityYears").textContent = String(years);
    }
    toggle(el("capacityBox"), plannedTotal > capacity);

    // Projects forward from contributions already made, net of money taken out.
    // This is a contribution base, not a market value: it deliberately ignores
    // growth earned to date, which the reader has not been asked for.
    const startingBalance = totalContributed - priorYearWithdrawals - thisYearWithdrawals;
    const yearlyData = projectGrowth({
      startingBalance: Math.max(startingBalance, 0),
      contributionAmount,
      contributionFrequency,
      returnRate,
      years
    });
    const finalYear = yearlyData[yearlyData.length - 1];

    el("totalAmount").textContent = formatCurrency(finalYear.totalValue);
    el("growthNote").textContent = `${years} ${years === 1 ? "year" : "years"} at ${returnRate}%, tax-free.`;
    el("totalContributions").textContent = formatCurrency(finalYear.totalContributions);
    el("totalGrowth").textContent = formatCurrency(finalYear.totalGrowth);

    // Illustrative only: what the growth would cost outside a TFSA. Uses a
    // mid-bracket 30% marginal rate on a 50% capital gains inclusion.
    el("taxSaved").textContent = formatCurrency(finalYear.totalGrowth * 0.5 * 0.30);

    const body = el("growthTableBody");
    body.innerHTML = "";
    const interval = years <= 10 ? 1 : 5;
    yearlyData.forEach((row) => {
      if (row.year === 0 || row.year % interval === 0 || row.year === years) {
        const tr = document.createElement("tr");
        tr.innerHTML = `
          <td class="year-col">${row.year === 0 ? "Today" : `Year ${row.year}`}</td>
          <td class="amount-col">${formatCurrency(row.totalValue)}</td>
          <td class="contributions-col">${formatCurrency(row.totalContributions)}</td>
          <td class="interest-col">${formatCurrency(row.totalGrowth)}</td>
        `;
        body.appendChild(tr);
      }
    });

    reveal();
  };

  document.querySelectorAll(".scenario-btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      Object.entries({
        birthYear: "birth",
        totalContributed: "contributed",
        priorWithdrawals: "withdrawn",
        thisYearWithdrawals: "withdrawn-now",
        contributionAmount: "amount",
        contributionFrequency: "frequency",
        returnRate: "rate",
        years: "years"
      }).forEach(([id, attr]) => {
        const value = btn.getAttribute(`data-${attr}`);
        if (value !== null) el(id).value = value;
      });
      render();
      blocks[0].scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  });

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    render();
    blocks[0].scrollIntoView({ behavior: "smooth", block: "nearest" });
  });

  // Show a real number on load rather than an empty panel.
  render();
};
